import { buildAuthHeaders } from '../core/http'

/**
 * Fetches TTS audio from the backend and plays it.
 * Supports an onPlaybackStart callback so the cursor can sync with audio.
 */
export class TTSPlayer {
  private apiBase: string
  private getAuthToken?: () => Promise<string>
  private currentAudio: HTMLAudioElement | null = null
  private abortController: AbortController | null = null

  constructor(apiBase: string, getAuthToken?: () => Promise<string>) {
    this.apiBase = apiBase
    this.getAuthToken = getAuthToken
  }

  /**
   * Speak the given text.
   * @param onPlaybackStart Called the moment audio actually begins playing
   */
  async speak(
    text: string,
    voice?: string,
    onPlaybackStart?: () => void,
    language?: string,
  ): Promise<void> {
    this.stop()

    const headers = await buildAuthHeaders(this.getAuthToken)

    this.abortController = new AbortController()

    const response = await fetch(`${this.apiBase}/tts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text, voice, language }),
      signal: this.abortController.signal,
    })

    if (!response.ok) {
      throw new Error(`TTS failed: ${response.status}`)
    }

    // Check if MediaSource supports mp3 for true streaming
    if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg')) {
      return this.playWithMediaSource(response, onPlaybackStart)
    }

    return this.playBuffered(response, onPlaybackStart)
  }

  private playWithMediaSource(response: Response, onPlaybackStart?: () => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const mediaSource = new MediaSource()
      const audio = new Audio()
      audio.src = URL.createObjectURL(mediaSource)
      this.currentAudio = audio

      mediaSource.addEventListener('sourceopen', async () => {
        const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg')
        const reader = response.body?.getReader()
        if (!reader) {
          reject(new Error('No response body'))
          return
        }

        let playStarted = false
        const pendingChunks: Uint8Array[] = []
        let streamDone = false

        const flushPending = () => {
          if (sourceBuffer.updating || pendingChunks.length === 0) return
          const chunk = pendingChunks.shift()!
          sourceBuffer.appendBuffer(chunk)
        }

        sourceBuffer.addEventListener('updateend', () => {
          if (!playStarted) {
            playStarted = true
            audio
              .play()
              .then(() => onPlaybackStart?.())
              .catch(reject)
          }
          if (pendingChunks.length > 0) {
            flushPending()
          } else if (streamDone && mediaSource.readyState === 'open') {
            mediaSource.endOfStream()
          }
        })

        audio.onended = () => {
          URL.revokeObjectURL(audio.src)
          this.currentAudio = null
          resolve()
        }
        audio.onerror = () => {
          URL.revokeObjectURL(audio.src)
          this.currentAudio = null
          reject(new Error('TTS playback failed'))
        }

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) {
              streamDone = true
              if (!sourceBuffer.updating && pendingChunks.length === 0) {
                if (mediaSource.readyState === 'open') {
                  mediaSource.endOfStream()
                }
              }
              break
            }
            if (value) {
              pendingChunks.push(value)
              flushPending()
            }
          }
        } catch (err) {
          if ((err as Error).name === 'AbortError') {
            resolve()
          } else {
            reject(err)
          }
        }
      })
    })
  }

  private async playBuffered(response: Response, onPlaybackStart?: () => void): Promise<void> {
    const audioBlob = await response.blob()
    const url = URL.createObjectURL(audioBlob)

    return new Promise<void>((resolve, reject) => {
      const audio = new Audio(url)
      this.currentAudio = audio

      audio.onplay = () => onPlaybackStart?.()
      audio.onended = () => {
        URL.revokeObjectURL(url)
        this.currentAudio = null
        resolve()
      }
      audio.onerror = () => {
        URL.revokeObjectURL(url)
        this.currentAudio = null
        reject(new Error('TTS playback failed'))
      }
      audio.play().catch(reject)
    })
  }

  stop(): void {
    this.abortController?.abort()
    this.abortController = null
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio = null
    }
  }

  get isPlaying(): boolean {
    return this.currentAudio !== null && !this.currentAudio.paused && !this.currentAudio.ended
  }
}
